import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";

import { Models } from "@/api/types";
import genericProviderImage from "@/assets/images/generic-provider.png";
import { Calendar } from "@/components/ui/calendar";
import { useTranslation } from "@/core/i18n";

interface BookingCalendarProps {
  providerName: string | null;
  onBackToProviders: () => void;
  providerSchedules: Models<"DisplayProviderAvailabilitySlot">[];
  selectedDate: Date | undefined;
  selectedTime: string | undefined;
  setSelectedDate: (date: Date | undefined) => void;
  setSelectedTime: (time: string | undefined) => void;
  isLoadingTimeSlots: boolean;
}

interface TimeSlot {
  time: string;
  isAvailable: boolean;
  isSelected?: boolean;
}

interface TimeSlotSelectionProps {
  selectedTime: string | undefined;
  providerSchedules: Models<"DisplayProviderAvailabilitySlot">[];
  setSelectedTime: (time: string | undefined) => void;
}

const TimeSlotSelection = ({
  selectedTime,
  providerSchedules,
  setSelectedTime,
}: TimeSlotSelectionProps) => {
  const { t } = useTranslation();
  const timeSlots = useMemo(() => {
    const allTimeSlots = providerSchedules.map((schedule) => ({
      time: dayjs(schedule.startTime).format("HH:mm"),
      isAvailable: schedule.isAvailable,
      isSelected: selectedTime === dayjs(schedule.startTime).format("HH:mm"),
    }));

    // Split into morning (8:00-11:30) and afternoon (12:00-17:00)
    const morning = allTimeSlots.filter((slot) => {
      const hour = parseInt(slot.time.split(":")[0]);
      return hour >= 8 && hour < 12;
    });

    const afternoon = allTimeSlots.filter((slot) => {
      const hour = parseInt(slot.time.split(":")[0]);
      return hour >= 12;
    });

    return { morning, afternoon };
  }, [providerSchedules, selectedTime]);

  const formatTimeDisplay = (time: string) => {
    const [hour, minute] = time.split(":").map(Number);
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minute.toString().padStart(2, "0")}`;
  };

  const TimeSlotSection = ({ title, slots }: { title: string; slots: TimeSlot[] }) => (
    <div className="mb-6">
      <h3 className="text-medium mb-3 text-gray-800">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {slots.map((slot) => (
          <button
            key={slot.time}
            onClick={() => slot.isAvailable && setSelectedTime(slot.time)}
            disabled={!slot.isAvailable}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              slot.isSelected
                ? "bg-brand-color text-white"
                : slot.isAvailable
                  ? "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                  : "cursor-not-allowed bg-gray-50 text-gray-400"
            }`}
          >
            {formatTimeDisplay(slot.time)}
          </button>
        ))}
      </div>
    </div>
  );

  if (!selectedTime && !providerSchedules.length) return null;

  return (
    <div className="mt-8 w-full px-4">
      {timeSlots.morning.length > 0 && (
        <TimeSlotSection title={t("bookingCalendar.morning")} slots={timeSlots.morning} />
      )}
      {timeSlots.afternoon.length > 0 && (
        <TimeSlotSection title={t("bookingCalendar.afternoon")} slots={timeSlots.afternoon} />
      )}
    </div>
  );
};

const BookingCalendar = ({
  providerName,
  onBackToProviders,
  selectedDate,
  selectedTime,
  setSelectedDate,
  setSelectedTime,
  providerSchedules,
  isLoadingTimeSlots,
}: BookingCalendarProps) => {
  const { t } = useTranslation();
  const [dateProviderSchedules, setDateProviderSchedules] = useState<
    Models<"DisplayProviderAvailabilitySlot">[]
  >([]);
  // Function to handle date selection
  const handleDateSelect = (date: Date | undefined) => {
    if (date && date !== selectedDate) {
      setSelectedDate(date);
      setSelectedTime(undefined);
    }
  };

  // Filter provider schedules for the selected date
  useEffect(() => {
    const dateStr = selectedDate?.toISOString().split("T")[0];
    const filteredSchedules = providerSchedules.filter((schedule) => schedule.date === dateStr);
    setDateProviderSchedules(filteredSchedules);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerSchedules, selectedDate]);

  // Function to determine if a date is disabled based on provider schedules
  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Disable past dates
    if (date < today) return true;

    // Check if there are any provider schedules for this date
    const dateStr = date.toISOString().split("T")[0];
    const hasSchedulesForDate = providerSchedules.some((schedule) => schedule.date === dateStr);

    // Disable the date if there are no schedules available
    return !hasSchedulesForDate;
  };

  return (
    <div className="w-full">
      {/* Selected Provider Tile */}
      <div className="mt-8 w-full">
        <div className="flex w-full items-center justify-center bg-gray-100 py-2">
          {/* Provider Picture */}
          {providerName && (
            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-gray-200">
              <img
                src={genericProviderImage}
                alt={t("bookingCalendar.providerProfileAlt", { providerName })}
                className="h-12 w-12 rounded-full object-cover"
              />
            </div>
          )}

          {/* Provider Info */}
          <div className="ml-4 flex flex-col">
            {providerName && <h3 className="text-sm font-normal text-gray-800">{providerName}</h3>}
            <button
              onClick={onBackToProviders}
              className="mt-1 text-left text-sm font-bold text-brand-color hover:underline"
            >
              {providerName
                ? t("bookingCalendar.changeProvider")
                : t("bookingCalendar.selectAProvider")}
            </button>
          </div>
        </div>
      </div>

      {/* Next Available Section */}
      <div className="mt-8 text-center text-lg text-sub1 font-medium uppercase text-brand-color">
        {t("bookingCalendar.nextAvailable")}
      </div>

      {/* Calendar */}
      <div className="mt-6 flex justify-center">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleDateSelect}
          disabled={isDateDisabled}
          className="rounded-md border border-gray-200 bg-gray-100 p-4 shadow-sm"
          classNames={{
            day: "m-1 h-10 w-10 text-base bg-white text-gray-900 border border-transparent rounded-md",
            selected: "text-orange-400 !border !border-orange-400",
            day_button: "!bg-white hover:!text-orange-400",
            disabled: "text-gray-400 bg-white",
            outside: "text-gray-400 bg-black",
          }}
        />
      </div>

      {/* Time Slot Selection */}
      {isLoadingTimeSlots ? (
        <div className="mt-8 flex justify-center">
          <div className="flex items-center space-x-2">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-brand-color"></div>
            <span className="text-sm text-gray-600">{t("bookingCalendar.loadingSchedules")}</span>
          </div>
        </div>
      ) : (
        <TimeSlotSelection
          selectedTime={selectedTime}
          providerSchedules={dateProviderSchedules}
          setSelectedTime={setSelectedTime}
        />
      )}
    </div>
  );
};

export default BookingCalendar;
